import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
// ----------------------------------------------------------------------

export default function MatchesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [countMatches, setCountMatches] = useState(0);
  const [openConfirm, setOpenConfirm] = useState(false);

  useEffect(() => {
    if (searchParams.has("type")) {
      const competition = searchParams.get("type");
      setCompetition(competition);
    }
  }, [searchParams]);

  const getRows = (competition) => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/admin/matches?competition=${competition}`)
      .then(function (response) {
        setLoading(false);
        setRows(response.data.matches);
      });
  };

  const fn = useCallback(() => getRows(competition), [competition])
  useEffect(() => {
    if(competition) fn(competition)
  }, [competition, fn])

  const submitGenerateMatches = () => {
    setLoading(true);
    setCountMatches(0);
    axios
      .post(`${process.env.REACT_APP_API_BASE_URL}/admin/generate-matches`, {
        countMatches,
        competition
      })
      .then(() => setLoading(false))
      .catch((err) => console.log("err", err))
      .finally(() => {
        getRows();
      });
  };

  const submitUpdateUserScore = () => {
    setLoading(true);
    setCountMatches(0);
    axios
      .post(`${process.env.REACT_APP_API_BASE_URL}/admin/leaderboard/score?competition=${competition}`)
      .then(() => setLoading(false))
      .catch((err) => console.log("err", err))
      .finally(() => {
        getRows();
      });
  };

  const submitClearMatches = () => {
    setLoading(true);
    setOpenConfirm(false)
    setCountMatches(0);
    axios
      .post(`${process.env.REACT_APP_API_BASE_URL}/admin/clear-matches`, {
        countMatches,
      })
      .then(() => setLoading(false))
      .catch((err) => console.log("err", err))
      .finally(() => {
        getRows();
      });
  };

  return (
    <>
      <Helmet>
        <title> Matches </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Matches
          </Typography>
          {rows.length !== null && rows.length > 0 ? (
            <Grid item alignItems="center">
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:circle-outline" />}
                onClick={() => submitUpdateUserScore()}
                sx={{ mr: 1 }}
              >
                Update User Score
              </Button>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:trash-2-outline" />}
                onClick={() => setOpenConfirm(true)}
              >
                Delete All Matches
              </Button>
            </Grid>
          ) : (
            <Grid item alignItems="center">
              <FormControl sx={{ marginRight: 1, minWidth: 120 }} size="small">
                <InputLabel id="demo-simple-select-label-home_team">
                  First Round
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label-home_team"
                  id="demo-simple-select-home_team"
                  value={countMatches}
                  name="home_team_id"
                  label="Home Team"
                  onChange={(event) => setCountMatches(event.target.value)}
                >
                  {[0, 1, 2, 4, 8, 16, 32, 64, 128].map((count) => (
                    <MenuItem value={count} key={`count_${count}`}>
                      {count}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
                disabled={countMatches === 0}
                onClick={submitGenerateMatches}
              >
                Generate Matches
              </Button>
            </Grid>
          )}
        </Stack>
        <Card>
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Slot</TableCell>
                      <TableCell>Match Feed</TableCell>
                      <TableCell>Conference</TableCell>
                      <TableCell>Round</TableCell>
                      <TableCell>Home Team</TableCell>
                      <TableCell>Away Team</TableCell>
                      <TableCell>Winning Team</TableCell>
                      <TableCell>Result Home</TableCell>
                      <TableCell>Result Away</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow>
                        <TableCell>{row.slot}</TableCell>
                        <TableCell>{row.matchFeedId}</TableCell>
                        <TableCell>{row.conferenceName}</TableCell>
                        <TableCell>{row.roundName}</TableCell>
                        <TableCell>{row.homeTeamName}</TableCell>
                        <TableCell>{row.awayTeamName}</TableCell>
                        <TableCell>
                          {row.winnerTeamId === row.homeTeamId && row.homeTeamId}
                          {row.winnerTeamId === row.awayTeamId && row.awayTeamId}
                        </TableCell>
                        <TableCell>{row.result?.homeScore}</TableCell>
                        <TableCell>{row.result?.awayScore}</TableCell>
                        <TableCell>
                          {row?.startTimeUtc
                            ? new Date(row.startTimeUtc).toLocaleDateString()
                            : ""}
                        </TableCell>
                        <TableCell align="right">
                          <Iconify
                            icon={"eva:edit-outline"}
                            sx={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate(
                                `/dashboard/edit-match?matchId=${row.matchId}&type=${competition}`
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
        <Dialog
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Are you sure you want to delete Matches?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              All matches would be deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
            <Button onClick={() => submitClearMatches(true)} autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
